import {
  Component,
  EventEmitter,
  Input,
  Output,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { ReservationStateService } from '../../restaurant-booking/state/reservation-state.service';
import { IReservation } from 'src/app/models/reservation.model';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { DataService } from 'src/app/services/data.service';

@Component({
  selector: 'app-number-input',
  templateUrl: './number-input.component.html',
  styleUrls: ['./number-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumberInputComponent),
      multi: true,
    },
  ],
})
export class NumberInputComponent implements ControlValueAccessor {
  @Input() value = 1;
  @Output() valueChange: EventEmitter<number> = new EventEmitter<number>();

  @Input() disabled = false;

  @Input() min?: number;
  @Input() max?: number;
  @Input() infant = false;
  @Input() isAsyaSoft: boolean;
  totalPersonPax: number;

  didDestroy$ = new Subject<void>();

  constructor(
    private toastController: ToastController,
    private translateService: TranslateService,
    public reservationState: ReservationStateService,
    private authServ: AuthService,
    private dataServ: DataService
  ) {}

  ngOnInit() {
    if(!this.value){
      this.value = 0;
    }
    
    this.reservationState.reservationData$
      .pipe(takeUntil(this.didDestroy$))
      .subscribe((data) => {
        if (this.dataServ.menuItem.reservation_number_of_people_restriction) {
          this.totalPersonPax = data.adults + data.children;
        } else {
          this.totalPersonPax = data.adults + data.children + data.infants;
        }
      });

    if (!this.min) {
      this.min = 0;
    }
    
  }

  writeValue(value: number): void {
    this.value = value;
  }

  registerOnChange(onChange: (value: number) => void): void {
    this.valueChange.subscribe(onChange);
  }

  registerOnTouched(onTouched: () => void): void {
    this.valueChange.subscribe(onTouched);
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  increment(): void {
    const MESSAGE = this.translateService.instant(
      'You have reached the maximum number that can be selected for this reservation.'
    );
    if (
      this.dataServ.menuItem?.reservation_number_of_people_restriction &&
      this.authServ.profile?.reservation_pax
    ) {
      if (this.infant) {
        this.value++;
        this.valueChange.emit(this.value);
        return;
      } else if (
        this.totalPersonPax >=
        Math.min(this.authServ.profile.reservation_pax, this.max)
      ) {
        this.toastController
          .create({
            color: 'warning',
            duration: 3500,
            message:
              MESSAGE +
              ` (${Math.min(this.authServ.profile.reservation_pax, this.max)})`,
            position: 'middle',
            buttons: [
              {
                text: this.translateService.instant('OK'),
                role: 'cancel',
              },
            ],
          })
          .then((toast) => toast.present());
        return;
      }
    } else if (this.max !== undefined && this.totalPersonPax >= this.max) {
      this.toastController
        .create({
          color: 'warning',
          duration: 3500,
          message: MESSAGE + ` (${this.max})`,
          position: 'middle',
          buttons: [
            {
              text: this.translateService.instant('OK'),
              role: 'cancel',
            },
          ],
        })
        .then((toast) => toast.present());
      return;
    }

    this.value++;
    this.valueChange.emit(this.value);
  }

  decrement(): void {
    const MESSAGE = this.translateService.instant(
      `Guest count can't be lower then minimum capacity.`
    );
    if (this.min !== undefined && this.totalPersonPax <= this.min) {
      this.toastController
        .create({
          color: 'warning',
          duration: 2000,
          message: MESSAGE + `(${this.min})`,
          position: 'middle',
          buttons: [
            {
              text: this.translateService.instant('OK'),
              role: 'cancel',
            },
          ],
        })
        .then((toast) => toast.present());
      return;
    }

    if (this.value > 0) {
      this.value--;
      this.valueChange.emit(this.value);
    }
  }
  ngOnDestroy() {
    this.didDestroy$.next();
    this.didDestroy$.complete();
  }
}
